import { useToken } from '@context/contexts';
import { useCallback, useMemo } from 'react';
import { stripTrailingSlashes } from '@utils/index';

// Хук для API запросов с использованием fetch
export const useApi = () => {
    const { token, clearToken, impersonationToken } = useToken();

    // Обработчик ошибок авторизации
    const handleAuthError = useCallback(() => {
        clearToken();
        window.location.href = '/login/';
    }, [clearToken]);

    const normalizeEndpoint = useCallback((endpoint, baseURL) => {
        if (!endpoint) {
            return '';
        }

        const baseHasApiSuffix = /\/api\/?$/.test(baseURL);
        let normalized = String(endpoint).trim();

        normalized = normalized.replace(/^\/+/, '');

        if (baseHasApiSuffix) {
            normalized = normalized.replace(/^api\//, '');
        }

        return normalized;
    }, []);

    // Вспомогательная функция для построения полного URL
    const buildUrl = useCallback((endpoint) => {
        const endpointString = String(endpoint || '');
        if (endpointString.startsWith('http://') || endpointString.startsWith('https://')) {
            return endpointString;
        }

        const baseURL = stripTrailingSlashes(String(import.meta.env.VITE_API_URL || ''));
        const normalizedEndpoint = normalizeEndpoint(endpointString, baseURL);

        if (!normalizedEndpoint) {
            return baseURL;
        }

        if (!baseURL) {
            return `/${normalizedEndpoint}`;
        }

        return `${baseURL}/${normalizedEndpoint}`;
    }, [normalizeEndpoint]);

    // Вспомогательная функция для получения заголовков
    const getHeaders = useCallback((customHeaders = {}) => {
        const headers = {
            'Content-Type': 'application/json',
            ...customHeaders,
        };
        // Prefer impersonation token if active, otherwise use main auth token
        const activeToken = impersonationToken || token;
        if (activeToken) {
            headers.Authorization = `Bearer ${activeToken}`;
        }
        return headers;
    }, [token, impersonationToken]);

    const parseResponseBody = useCallback(async (response) => {
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
            const text = await response.text();
            return text || null;
        }

        try {
            return await response.json();
        } catch {
            return null;
        }
    }, []);

    const createApiError = useCallback((response, url, responseBody) => {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.name = 'ApiError';
        error.status = response.status;
        error.statusText = response.statusText;
        error.url = url;
        error.responseBody = responseBody;
        return error;
    }, []);

    const request = useCallback(async (endpoint, options = {}) => {
        const url = buildUrl(endpoint);
        const response = await fetch(url, {
            ...options,
            headers: getHeaders(options.headers),
        });

        if (response.status === 401) {
            handleAuthError();
            return null;
        }

        const responseBody = await parseResponseBody(response);

        if (!response.ok) {
            throw createApiError(response, url, responseBody);
        }

        return responseBody;
    }, [buildUrl, createApiError, getHeaders, handleAuthError, parseResponseBody]);

    // Универсальный метод для GET запросов
    const get = useCallback(async (endpoint, options = {}) => {
        return request(endpoint, {
            ...options,
            method: 'GET',
        });
    }, [request]);

    // Универсальный метод для POST запросов
    const post = useCallback(async (endpoint, data = {}, options = {}) => {
        return request(endpoint, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
        });
    }, [request]);

    // Универсальный метод для PUT запросов
    const put = useCallback(async (endpoint, data = {}, options = {}) => {
        return request(endpoint, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }, [request]);

    const patch = useCallback(async (endpoint, data = {}, options = {}) => {
        return request(endpoint, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }, [request]);

    // Универсальный метод для DELETE запросов
    const del = useCallback(async (endpoint, options = {}) => {
        return request(endpoint, {
            ...options,
            method: 'DELETE',
        });
    }, [request]);

    return useMemo(() => ({
        get,
        post,
        put,
        patch,
        delete: del,
    }), [get, post, put, patch, del]);
};
